import { DOCUMENT } from "@angular/common";
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { NotificationService } from "src/app/shared/isolutions-ui/feedback/notifications/notification.service";
import { ProcessingLog } from "src/app/core/models/processingLog.model";
import { UserUpdate } from "src/app/core/models/user.model";
import { ProcessingLogService } from "src/app/core/services/processingLog.service";
import { ServiceProviderService } from "src/app/core/services/serviceProvider.service";
import { UserService } from "src/app/core/services/user.service";
import { WhiteLabelService } from "src/app/core/services/whitelabel.service";
import { ThemeService } from "src/app/core/services/theme.service";
import { environment } from "../../../environments/environment";
import { AuthenticationService } from "../../core/services/auth.service";
import { AuthfakeauthenticationService } from "../../core/services/authfake.service";
import { LanguageService } from "../../core/services/language.service";

interface Language {
  text: string;
  flag: string;
  lang: string;
  value: number;
}

@Component({
  standalone: false,
  selector: "app-topbar",
  templateUrl: "./topbar.component.html",
  styleUrls: ["./topbar.component.scss"],
})
export class TopbarComponent implements OnInit, OnDestroy {
  username: string | undefined;
  profile: string | undefined;
  user: any;
  element: any;
  cookieValue: string | undefined;
  countryName: string | undefined;
  flagvalue: string | undefined;
  serviceProviders: any[] = [];
  selectedserviceProvider: any;
  version: string = environment.version;
  serviceProviderIdSelected: any;
  openMobileMenu: boolean | undefined;
  unreadCount: number = 0;
  recentLogs: ProcessingLog[] = [];
  langExpanded = false;
  private notificationInterval: any;

  get isAdmin(): boolean {
    return this.user?.profile?.profileId === 1;
  }

  listLang: Language[] = [
    {
      text: "Português",
      flag: "assets/images/flags/br.png",
      lang: "pt",
      value: 1,
    },
    {
      text: "English",
      flag: "assets/images/flags/us.jpg",
      lang: "en",
      value: 2,
    },
    {
      text: "Español",
      flag: "assets/images/flags/spain.jpg",
      lang: "es",
      value: 3,
    },
  ];

  @Output() settingsButtonClicked = new EventEmitter();
  @Output() mobileMenuButtonClicked = new EventEmitter();

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private authService: AuthenticationService,
    private authFackservice: AuthfakeauthenticationService,
    public languageService: LanguageService,
    public translate: TranslateService,
    public serviceProviderService: ServiceProviderService,
    public userService: UserService,
    public ngxSpinner: NgxSpinnerService,
    private notify: NotificationService,
    private processingLogService: ProcessingLogService,
    public wl: WhiteLabelService,
    private themeService: ThemeService,
  ) {}

  ngOnInit() {
    this.openMobileMenu = false;
    this.element = document.documentElement;
    this.getUserName();
    this.initializeLanguage();
    this.loadUnreadCount();
    this.notificationInterval = setInterval(() => {
      this.loadUnreadCount();
    }, 60000);
  }

  ngOnDestroy() {
    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
    }
  }

  /** Toggle de tema */
  get themeIcon(): string {
    return this.themeService.effectiveTheme === 'dark' ? 'bx-sun' : 'bx-moon';
  }

  get themeLabel(): string {
    return this.themeService.effectiveTheme === 'dark'
      ? this.translate.instant('TOPBAR.LIGHT_THEME')
      : this.translate.instant('TOPBAR.DARK_THEME');
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  private dismissedKey = 'dismissedNotifications';

  loadUnreadCount() {
    this.processingLogService.getUnreadCount().subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          this.unreadCount = response.data[0] || 0;
        }
      },
      error: () => {},
    });
  }

  onNotificationBellClick() {
    this.processingLogService
      .filterProcessingLogs({})
      .subscribe({
        next: (response) => {
          if (response.isSuccessful) {
            const dismissed = this.getDismissedIds();
            this.recentLogs = (response.data || [])
              .filter((log: ProcessingLog) => !dismissed.includes(log.processingLogId))
              .slice(0, 10);
          }
        },
        error: () => {},
      });
  }

  onNotificationClick(log: ProcessingLog) {
    if (!log.isRead) {
      this.processingLogService.markAsRead(log.processingLogId).subscribe({
        next: () => {
          log.isRead = true;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        },
      });
    }
    this.navigateToRelatedItem(log);
  }

  private navigateToRelatedItem(log: ProcessingLog) {
    if (log.routeId) {
      this.router.navigateByUrl(`/routes/view;id=${log.routeId}`);
    } else if (log.serviceOrderTrackerId) {
      this.router.navigateByUrl(`/registration/service-orders/detail;id=${log.serviceOrderTrackerId}`);
    } else {
      this.router.navigateByUrl('/routes/all');
    }
  }

  dismissNotification(log: ProcessingLog, event: Event) {
    event.stopPropagation();
    const dismissed = this.getDismissedIds();
    dismissed.push(log.processingLogId);
    localStorage.setItem(this.dismissedKey, JSON.stringify(dismissed));
    this.recentLogs = this.recentLogs.filter((l) => l.processingLogId !== log.processingLogId);
    if (!log.isRead) {
      this.processingLogService.markAsRead(log.processingLogId).subscribe({
        next: () => {
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        },
      });
    }
  }

  clearAllNotifications() {
    const dismissed = this.getDismissedIds();
    this.recentLogs.forEach((log) => {
      if (!dismissed.includes(log.processingLogId)) {
        dismissed.push(log.processingLogId);
      }
    });
    localStorage.setItem(this.dismissedKey, JSON.stringify(dismissed));
    this.recentLogs = [];
    this.markAllNotificationsAsRead();
  }

  private getDismissedIds(): number[] {
    try {
      const stored = localStorage.getItem(this.dismissedKey);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  markAllNotificationsAsRead() {
    this.processingLogService.markAllAsRead().subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          this.unreadCount = 0;
          this.recentLogs.forEach((log) => (log.isRead = true));
        }
      },
    });
  }

  initializeLanguage() {
    if (this.user?.language !== null && this.user?.language !== undefined) {
      const userLang = this.listLang.find(
        (x) => x.value === this.user.language,
      );
      if (userLang) {
        this.cookieValue = userLang.lang;
      }
    }

    if (!this.cookieValue) {
      this.cookieValue = "pt";
    }

    const val = this.listLang.filter((x) => x.lang === this.cookieValue);
    this.countryName = val.map((element) => element.text)[0];

    if (val.length === 0) {
      this.flagvalue = "assets/images/flags/br.png";
    } else {
      this.flagvalue = val.map((element) => element.flag)[0];
    }

    this.languageService.setLanguage(this.cookieValue);
  }

  getUserName() {
    const user = this.authFackservice.getUser();
    this.username = user?.name;
    this.profile = user?.profileWkf;
    this.user = user;

    this.getServiceProviders();
  }

  getServiceProviders() {
    this.serviceProviderService
      .getServiceProvidersByTenant(this.user.tenant.tenantId)
      .subscribe((response) => {
        this.serviceProviders = response.data.filter((sp) => sp.flActive);

        const storedId = localStorage.getItem("serviceProviderIdSelected");
        const parsedStoredId = storedId ? JSON.parse(storedId) : null;

        const availableProviders = this.serviceProviders;

        if (
          parsedStoredId &&
          availableProviders.find(
            (sp) => sp.serviceProviderId === parsedStoredId,
          )
        ) {
          this.serviceProviderIdSelected = parsedStoredId;
        } else if (
          this.user?.serviceProvider?.serviceProviderId &&
          availableProviders.find(
            (sp) =>
              sp.serviceProviderId ===
              this.user.serviceProvider.serviceProviderId,
          )
        ) {
          this.serviceProviderIdSelected =
            this.user.serviceProvider.serviceProviderId;
          localStorage.setItem(
            "serviceProviderIdSelected",
            JSON.stringify(this.serviceProviderIdSelected),
          );
        } else if (availableProviders.length > 0) {
          this.serviceProviderIdSelected =
            availableProviders[0].serviceProviderId;
          localStorage.setItem(
            "serviceProviderIdSelected",
            JSON.stringify(this.serviceProviderIdSelected),
          );
        }
      });
  }

  onServiceProviderChange(event: any) {
    const selectedId = event;
    const previousId = this.serviceProviderIdSelected;
    this.serviceProviderIdSelected = selectedId;

    this.ngxSpinner.show();

    const providersList = this.serviceProviders;
    const provider = providersList.find(
      (sp) => sp.serviceProviderId === selectedId,
    );

    if (!provider) {
      this.serviceProviderIdSelected = previousId;
      this.handleServiceProviderError(this.translate.instant("TOPBAR.ERRORS.SERVICE_PROVIDER_NOT_FOUND"));
      return;
    }

    this.selectedserviceProvider = provider;
    const userUpdate = this.createUserUpdate(this.user);

    this.userService.updateUser(this.user.userId, userUpdate).subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          this.user.serviceProvider.serviceProviderId =
            provider.serviceProviderId;

          localStorage.setItem(
            "serviceProviderIdSelected",
            JSON.stringify(provider.serviceProviderId),
          );

          setTimeout(() => {
            window.location.reload();
          }, 100);
        } else {
          this.serviceProviderIdSelected = previousId;
          this.handleServiceProviderError(response.messageDescription);
        }
      },
      error: (err) => {
        this.serviceProviderIdSelected = previousId;
        this.handleServiceProviderError(
          this.translate.instant("TOPBAR.ERRORS.UPDATE_SERVICE_PROVIDER"),
        );
      },
    });
  }

  private handleServiceProviderError(message: string) {
    this.notify.error(message);
    this.ngxSpinner.hide();
  }

  createUserUpdate(user: any): UserUpdate {
    const userUpdate = new UserUpdate();
    userUpdate.serviceProviderId =
      this.selectedserviceProvider.serviceProviderId;
    userUpdate.flActive = true;
    return userUpdate;
  }

  setLanguage(text: string, lang: string, flag: string, value: number) {
    this.countryName = text;
    this.flagvalue = flag;
    this.cookieValue = lang;
    this.languageService.setLanguage(lang);
    this.saveUserLanguage(value);
  }

  saveUserLanguage(languageValue: number) {
    this.ngxSpinner.show();

    const userUpdate = new UserUpdate();
    userUpdate.serviceProviderId = this.user.serviceProvider.serviceProviderId;
    userUpdate.flActive = true;
    userUpdate.language = languageValue;

    this.userService.updateUser(this.user.userId, userUpdate).subscribe({
      next: (response) => {
        if (response.isSuccessful) {
          this.user = response.data[0];
          this.authFackservice.updateUserLanguage(this.user);
          this.user.language = languageValue;

          this.notify.success(
            this.translate.instant("TOAST.LANGUAGE_CHANGED_SUCCESS"),
          );

          setTimeout(() => {
            window.location.reload();
          }, 500);
        } else {
          this.notify.error(response.messageDescription);
          this.ngxSpinner.hide();
        }
      },
      error: (err) => {
        this.notify.error(this.translate.instant("TOPBAR.ERRORS.UPDATE_LANGUAGE"));
        this.ngxSpinner.hide();
      },
    });
  }

  toggleRightSidebar() {
    this.settingsButtonClicked.emit();
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  logout() {
    localStorage.removeItem("serviceProviderIdSelected");

    if (environment.defaultauth === "firebase") {
      this.authService.logout();
    } else {
      this.authFackservice.logout();
    }
    this.router.navigate(["/account/login"]);
  }

  fullscreen() {
    document.body.classList.toggle("fullscreen-enable");
    if (
      !document.fullscreenElement &&
      !this.element.mozFullScreenElement &&
      !this.element.webkitFullscreenElement
    ) {
      if (this.element.requestFullscreen) {
        this.element.requestFullscreen();
      } else if (this.element.mozRequestFullScreen) {
        this.element.mozRequestFullScreen();
      } else if (this.element.webkitRequestFullscreen) {
        this.element.webkitRequestFullscreen();
      } else if (this.element.msRequestFullscreen) {
        this.element.msRequestFullscreen();
      }
    } else {
      if (this.document.exitFullscreen) {
        this.document.exitFullscreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
    }
  }
}
